import {
  teacherIdAtom,
  teacherProfileAtom,
  selectedLiveClassFeedbackClassroomIdAtom,
  selectedLiveClassFeedbackStudentIdAtom,
  selectedLiveClassFeedbackLessonIdAtom,
} from '@openeducation/oe-rn-jotai-atoms';
import {
  useEnvironmentContext,
  useLiveClassActiveFeedback,
  useLiveClassLesson,
  useStudentProfileLite,
} from '@openeducation/oe-rn-providers';
import {
  FeedbacksService,
  LiveClassFeedback,
} from '@openeducation/oe-rn-services';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';

import { usePcFeedbackFormReducer } from './pc-feedback-form-reducer';

const GENERAL_ERROR_MESSAGE =
  '**Oops!** Your feedback was not sent. Try again please!';

export const usePcFeedbackForm = () => {
  const studentId = useAtomValue(selectedLiveClassFeedbackStudentIdAtom);
  const studentLessonId = useAtomValue(selectedLiveClassFeedbackLessonIdAtom);
  const classroomId = useAtomValue(selectedLiveClassFeedbackClassroomIdAtom);
  const teacherId = useAtomValue(teacherIdAtom);
  const { environment } = useEnvironmentContext();
  const teacherProfile = useAtomValue(teacherProfileAtom);

  const { isLoadingLiveClassFeedback, liveClassFeedback } =
    useLiveClassActiveFeedback({
      classroomId,
      teacherFacultyId: teacherProfile?.sfdcFacultyId,
    });

  const [isError, setIsError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { studentProfile, isLoadingStudentProfile } = useStudentProfileLite({
    studentId,
  });

  const {
    liveClassLessons,
    isErrorLiveClassLessons,
    isLoadingLiveClassLessons,
  } = useLiveClassLesson({
    isTeacher: true,
    contactId: studentProfile?.contactId,
  });

  const {
    teacherFeedback,
    isLessonSubmitted,
    feedbackInit,
    lessonSelected,
    lessonSubmitted,
    restoreSubmittedLesson,
    lessonReset,
    lessonId,
  } = usePcFeedbackFormReducer();

  useEffect(() => {
    if (!classroomId || !teacherId || !studentId) {
      return;
    }

    const lessonId = liveClassFeedback?.lessonId || studentLessonId;
    if (lessonId && liveClassLessons) {
      restoreSubmittedLesson({
        classroomId,
        teacherId,
        studentId,
        lessonId,
      });
    } else {
      feedbackInit({ classroomId, teacherId, studentId });
    }
    setIsError(false);
  }, [
    classroomId,
    feedbackInit,
    liveClassFeedback?.lessonId,
    liveClassLessons,
    studentId,
    restoreSubmittedLesson,
    teacherId,
    liveClassFeedback,
    studentLessonId,
  ]);

  const submitLightFeedback = async (lessonId?: number) => {
    return submit({
      lessonId,
      classroomId,
      teacherId,
      studentId,
    });
  };

  const submitFeedback = async (
    teacherFeedback: string,
    lessonId: number,
    repeatRecommend?: boolean,
    criteriaPerformanceIds?: number[]
  ) => {
    if (!teacherFeedback) {
      setIsError(true);
      throw new Error("Can't submit feedback without teacher feedback");
    }

    return submit({
      nextLessonId: repeatRecommend ? lessonId : undefined,
      lessonId,
      classroomId,
      teacherId,
      studentId,
      teacherFeedback,
      criteriaPerformanceIds,
    });
  };

  const submit = async (feedback: Partial<LiveClassFeedback>) => {
    if (!feedback.lessonId) {
      setIsError(true);
      throw new Error("Can't submit feedback without lessonId");
    }
    if (!feedback.classroomId) {
      setIsError(true);
      throw new Error("Can't submit feedback without classroomId");
    }
    if (!feedback.studentId) {
      setIsError(true);
      throw new Error("Can't submit feedback without studentId");
    }
    if (!feedback.teacherId) {
      setIsError(true);
      throw new Error("Can't submit feedback without teacherId");
    }

    setIsSubmitting(true);
    try {
      await FeedbacksService.submitFeedback(
        environment,
        feedback as LiveClassFeedback
      );
    } catch (error) {
      setIsError(true);
      console.log(error);
      throw new Error('Error submitting feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLessonSelected = (lessonId: number) => {
    lessonReset();
    lessonSelected(lessonId);
  };

  return {
    liveClassLessons,
    teacherFeedback,
    lessonId,
    isLessonSubmitted,
    lessonSelected: handleLessonSelected,
    lessonSubmitted,
    submitFeedback,
    submitLightFeedback,
    error: isErrorLiveClassLessons || isError ? GENERAL_ERROR_MESSAGE : '',
    isSubmitting,
    isLoading:
      isLoadingLiveClassFeedback ||
      isLoadingLiveClassLessons ||
      isLoadingStudentProfile,
  };
};
